// Cave84 — Cost/Market basis toggle for region & cellar breakdowns (WI-3).
// Default is 'market': breakdown figures reflect live market value unless
// the owner explicitly switches to cost basis. Persisted in localStorage —
// this is a display preference, not sensitive, so (unlike the privacy
// reveal switch, which is sessionStorage-only) it should survive reloads.

import { create } from 'zustand';

export type ValuationBasis = 'cost' | 'market';

const STORAGE_KEY = 'cave84.valuationBasis';

function readInitialBasis(): ValuationBasis {
  if (typeof window === 'undefined') return 'market';
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'cost' ? 'cost' : 'market';
  } catch {
    return 'market';
  }
}

function persist(basis: ValuationBasis): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, basis);
  } catch {
    // Ignore — worst case the preference doesn't survive across sessions.
  }
}

interface ValuationBasisState {
  basis: ValuationBasis;
  setBasis: (basis: ValuationBasis) => void;
  toggle: () => void;
}

export const useValuationBasisStore = create<ValuationBasisState>((set) => ({
  basis: readInitialBasis(),
  setBasis: (basis) => set(() => { persist(basis); return { basis }; }),
  toggle: () => set((s) => {
    const next: ValuationBasis = s.basis === 'market' ? 'cost' : 'market';
    persist(next);
    return { basis: next };
  }),
}));
